import { useQuery } from "@tanstack/react-query";

export interface UnifiedFilterCountsParams {
  dates?: string[];
  sources?: string[];
  excludedSources?: string[];
  categories?: string[];
  excludedCategories?: string[];
  timePeriods?: string[];
  regions?: string[];
  excludedRegions?: string[];
}

export interface UnifiedFilterCountsResponse {
  sourceCounts: Record<string, number>;
  categoryCounts: Record<string, number>;
  regionCounts: Record<string, number>;
  totalEvents: number;
}

function buildStableKey(params: UnifiedFilterCountsParams): string {
  return JSON.stringify({
    d: params.dates?.slice().sort(),
    s: params.sources?.slice().sort(),
    es: params.excludedSources?.slice().sort(),
    c: params.categories?.slice().sort(),
    ec: params.excludedCategories?.slice().sort(),
    tp: params.timePeriods?.slice().sort(),
    r: params.regions?.slice().sort(),
    er: params.excludedRegions?.slice().sort(),
  });
}

export function useUnifiedFilterCounts(params: UnifiedFilterCountsParams = {}) {
  const stableKey = buildStableKey(params);

  return useQuery<UnifiedFilterCountsResponse>({
    queryKey: ['/api/filters/counts', stableKey],
    queryFn: async () => {
      const body: Record<string, any> = {};
      if (params.dates && params.dates.length > 0) body.dates = params.dates;
      if (params.sources && params.sources.length > 0) body.sources = params.sources;
      if (params.excludedSources && params.excludedSources.length > 0) body.excludedSources = params.excludedSources;
      if (params.categories && params.categories.length > 0) body.categories = params.categories;
      if (params.excludedCategories && params.excludedCategories.length > 0) body.excludedCategories = params.excludedCategories;
      if (params.timePeriods && params.timePeriods.length > 0) body.timePeriods = params.timePeriods;
      if (params.regions && params.regions.length > 0) body.regions = params.regions;
      if (params.excludedRegions && params.excludedRegions.length > 0) body.excludedRegions = params.excludedRegions;

      const res = await fetch('/api/filters/counts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Failed to fetch filter counts: ${res.status}`);
      return res.json();
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}
